import React, { ReactNode } from "react";
import {
  WhmTextField,
  WhmInputAdornment,
  WhmIconButton,
} from "@securitize/reactjs-whm";
import { FormikProps, useFormikContext, getIn } from "formik";
import { ReactComponent as VisibilityOff } from "@securitize/assets-sec-ui/dist/icons/svg/icon-eye-hide.svg";
import { ReactComponent as VisibilityOn } from "@securitize/assets-sec-ui/dist/icons/svg/icon-eye-show.svg";
import { getSensitiveFieldMaskProps } from "../../utils/maskUtils";

interface HiddenFieldProps<FormType> {
  id: string;
  label: React.ReactNode;
  field: string;
  showField: boolean;
  sx?: object;
  setShowField: React.Dispatch<React.SetStateAction<boolean>>;
  handleFieldChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    formik: FormikProps<FormType>,
    field: string,
  ) => Promise<void>;
  error?: boolean;
  helperText?: ReactNode;
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  disabled?: boolean;
  maskDisabled?: boolean;
  /**
   * `credential` (default) masks with `type="password"`, so the browser's
   * password manager participates — correct for real password fields.
   *
   * `sensitiveData` masks with CSS instead, keeping the input `type="text"` so
   * Chrome never classifies it as a password field: no autofill of saved
   * credentials, no "Suggest Strong Password", no save prompt. `autocomplete`
   * alone is not enough — Chrome claims any password input regardless.
   */
  fieldKind?: "credential" | "sensitiveData";
}

const HiddenField = <FormType extends object>({
  id,
  label,
  field,
  sx,
  showField,
  setShowField,
  handleFieldChange,
  error,
  helperText,
  onBlur,
  disabled,
  maskDisabled,
  fieldKind = "credential",
}: HiddenFieldProps<FormType>) => {
  const formik = useFormikContext<FormType>();

  const isMasked = !maskDisabled && !showField;

  // A real password field must stay `type="password"` so the password manager
  // can participate; a sensitive non-credential field must escape it entirely.
  const maskProps =
    fieldKind === "sensitiveData"
      ? getSensitiveFieldMaskProps(isMasked)
      : {
          type: isMasked ? ("password" as const) : ("text" as const),
          autoComplete: "off",
          sx: {},
        };

  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const value = getIn(formik.values, field) as string;
  const touched = getIn(formik.touched, field) as boolean;
  const errorMessage = getIn(formik.errors, field) as string;

  return (
    <WhmTextField
      name={field.toString()}
      type={maskProps.type}
      value={value}
      onChange={
        handleFieldChange
          ? (e) => {
              handleFieldChange(e, formik, field).catch((err) =>
                console.error(err),
              );
            }
          : formik.handleChange
      }
      helperText={helperText}
      disabled={disabled}
      error={error || (touched && Boolean(errorMessage))}
      onBlur={onBlur || formik.handleBlur}
      inputProps={{
        "data-test-id": `${id}-input`,
        autoComplete: maskProps.autoComplete,
      }}
      InputProps={{
        endAdornment: !maskDisabled && (
          <WhmInputAdornment position="end">
            <WhmIconButton
              onClick={() => setShowField((show) => !show)}
              onMouseDown={handleMouseDown}
              edge="end"
            >
              {showField ? (
                <VisibilityOff width={24} height={24} fill="currentColor" />
              ) : (
                <VisibilityOn width={24} height={24} fill="currentColor" />
              )}
            </WhmIconButton>
          </WhmInputAdornment>
        ),
      }}
      label={label}
      sx={{ ...sx, ...maskProps.sx }}
    />
  );
};

export default HiddenField;
